<template>
  <div id="etw_banner">
    <!-- Mobile banner -->
    <div class="banner_mobile" v-if="slides.length > 0"
      :style="slides[0].image ? { background: 'url(' + slides[0].image + ') center/cover' } : { background: slides[0].bg }">
      <h1 style="color:#fff;font-family:Oswald,sans-serif;font-size:1.8rem;margin-bottom:1rem">
        {{ slides[0].title }}
      </h1>
      <p v-if="slides[0].sub" style="color:rgba(255,255,255,.85);font-size:.9rem">{{ slides[0].sub }}</p>
      <a v-if="slides[0].btnText" :href="slides[0].link || '#'" class="btn_more" style="margin-top:1rem;display:inline-block">{{ slides[0].btnText }}</a>
    </div>
    <!-- Desktop slider -->
    <div class="swiper" ref="swiper">
      <div class="swiper-slide" v-for="(slide, index) in slides" :key="index"
        :class="{ active: index === currentIndex }"
        :style="slide.image ? { backgroundImage: 'url(' + slide.image + ')', backgroundSize: 'cover', backgroundPosition: 'center' } : { background: slide.bg }">
        <div class="banner_txt" :style="getTxtStyle(slide)">
          <h1 :class="slide.titleAnim" :style="slide.titleColor ? { color: slide.titleColor } : {}">{{ slide.title }}</h1>
          <span v-if="slide.sub" :class="slide.descAnim" :style="{ ...(slide.subtitleColor ? { color: slide.subtitleColor } : {}), whiteSpace: 'pre-line' }">{{ slide.sub }}</span>
          <a v-if="slide.btnText" :href="slide.link || '#'" class="btn_more banner-btn" :class="slide.btnAnim" :style="slide.btnColor ? { color: slide.btnColor, borderColor: slide.btnColor } : {}">{{ slide.btnText }}</a>
        </div>
      </div>
      <button class="swiper-button-next" @click="next">&#10095;</button>
      <button class="swiper-button-prev" @click="prev">&#10094;</button>
      <div class="swiper-pagination">
        <span v-for="(_, index) in slides" :key="index"
          class="swiper-pagination-bullet"
          :class="{ active: index === currentIndex }"
          @click="goTo(index)">
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { getBanners } from '@/api/banner'

export default {
  name: 'BannerSlider',
  data() {
    return {
      currentIndex: 0,
      timer: null,
      slides: []
    }
  },
  mounted() {
    this.loadBanners()
    this.startAutoPlay()
  },
  watch: {
    currentIndex() {
      this.$nextTick(() => this.triggerAnimations())
    }
  },
  beforeDestroy() {
    if (this.timer) clearInterval(this.timer)
  },
  methods: {
    getTxtStyle(slide) {
      const x = slide.textX ?? 50
      const y = slide.textY ?? 50
      let textAlign = 'center'
      if (x < 30) textAlign = 'left'
      else if (x > 70) textAlign = 'right'
      return {
        top: y + '%',
        left: x + '%',
        transform: 'translate(-' + x + '%, -' + y + '%)',
        textAlign
      }
    },
    getImageSrc(path) {
      return path || ''
    },
    async loadBanners() {
      try {
        const res = await getBanners()
        const data = Array.isArray(res) ? res : (res.data || [])
        if (data.length > 0) {
          this.slides = data.map((b, i) => ({
            image: b.image ? this.getImageSrc(b.image) : '',
            bg: i % 2 === 0
              ? 'linear-gradient(135deg,#1a5c1a 0%,#2d8a2d 40%,#46b200 100%)'
              : 'linear-gradient(135deg,#0d3d0d 0%,#1a6b1a 50%,#3a9600 100%)',
            title: b.title || '',
            titleColor: b.title_color || '',
            sub: b.subtitle || '',
            subtitleColor: b.subtitle_color || '',
            link: b.link || '',
            btnText: b.btn_text || '',
            btnColor: b.btn_color || '',
            textX: b.text_x ?? 50,
            textY: b.text_y ?? 50,
            titleAnim: 'ani slideInLeft',
            descAnim: 'ani slideInRight delay-1',
            btnAnim: 'ani slideInRight delay-2',
          }))
        } else {
          this.setDefaultSlides()
        }
        this.$nextTick(() => this.triggerAnimations())
      } catch (e) {
        this.setDefaultSlides()
        this.$nextTick(() => this.triggerAnimations())
      }
    },
    setDefaultSlides() {
      this.slides = [
        {
          image: '',
          bg: 'linear-gradient(135deg,#1a5c1a 0%,#2d8a2d 40%,#46b200 100%)',
          title: 'Biodegradable Products',
          sub: 'for Foodservice',
          link: '',
          btnText: '',
          textX: 50,
          textY: 50,
          titleAnim: 'ani slideInLeft',
          descAnim: 'ani slideInRight delay-1',
          btnAnim: 'ani slideInRight delay-2'
        },
        {
          image: '',
          bg: 'linear-gradient(135deg,#0d3d0d 0%,#1a6b1a 50%,#3a9600 100%)',
          title: 'Biodegradable and compostable',
          sub: 'minimizing the impact on the environment in comparison to single-use plastic',
          link: '',
          btnText: '',
          textX: 50,
          textY: 50,
          titleAnim: 'ani slideInLeft',
          descAnim: 'ani slideInRight delay-1',
          btnAnim: 'ani slideInRight delay-2'
        }
      ]
    },
    goTo(index) {
      this.currentIndex = index
      this.resetAutoPlay()
    },
    next() {
      this.currentIndex = (this.currentIndex + 1) % this.slides.length
    },
    prev() {
      this.currentIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length
    },
    resetAutoPlay() {
      if (this.timer) clearInterval(this.timer)
      this.startAutoPlay()
    },
    startAutoPlay() {
      this.timer = setInterval(() => {
        this.next()
      }, 5000)
    },
    triggerAnimations() {
      // Reset all slides' animations
      this.$el.querySelectorAll('.swiper-slide .ani').forEach(el => {
        el.classList.remove('animated')
      })
      // Trigger animation on active slide
      this.$nextTick(() => {
        const active = this.$el.querySelector('.swiper-slide.active .ani')
        if (active) {
          // Force reflow to restart animation
          void this.$el.querySelector('.swiper-slide.active').offsetWidth
          this.$el.querySelectorAll('.swiper-slide.active .ani').forEach(el => {
            el.classList.add('animated')
          })
        }
      })
    }
  }
}
</script>
